import { useAuth } from '@/hooks/useAuth';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { getIntegrationOnboardingSessions } from '@heidi/integrations/connected-apps/data';
import {
  getUserEHRLaunchProfile,
  getUserEHRLaunchProfileAuth0,
} from '@heidi/integrations/ehr/data';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { IntegrationDebugJsonResult } from './integration-debug-json-result';

const messages = defineMessages({
  heading: {
    defaultMessage: 'Integration account data',
    id: 'components.dev.integration.account.heading',
  },
  description: {
    defaultMessage:
      "Fetches all integration onboarding sessions and legacy EHR launch profiles for the signed-in user. The profile request automatically matches the user's auth provider.",
    id: 'components.dev.integration.account.description',
  },
  authProvider: {
    defaultMessage: 'Detected auth provider: {provider}',
    id: 'components.dev.integration.account.authProvider',
  },
  getOnboardingSessions: {
    defaultMessage: 'Get integration onboarding sessions',
    id: 'components.dev.integration.account.getOnboardingSessions',
  },
  getEhrLaunchProfiles: {
    defaultMessage: 'Get EHR user launch profiles',
    id: 'components.dev.integration.account.getEhrLaunchProfiles',
  },
  onboardingResult: {
    defaultMessage: 'Integration onboarding sessions',
    id: 'components.dev.integration.account.onboardingResult',
  },
  launchProfileResult: {
    defaultMessage: 'EHR user launch profiles',
    id: 'components.dev.integration.account.launchProfileResult',
  },
  onboardingError: {
    defaultMessage: 'Could not fetch integration onboarding sessions.',
    id: 'components.dev.integration.account.onboardingError',
  },
  launchProfileError: {
    defaultMessage: 'Could not fetch EHR user launch profiles.',
    id: 'components.dev.integration.account.launchProfileError',
  },
});

interface RequestState {
  data?: unknown;
  error?: string;
  isLoading: boolean;
}

const INITIAL_REQUEST_STATE: RequestState = { isLoading: false };

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

export function IntegrationAccountDevSection() {
  const intl = useIntl();
  const { provider } = useAuth();
  const [onboardingState, setOnboardingState] =
    useState<RequestState>(INITIAL_REQUEST_STATE);
  const [launchProfileState, setLaunchProfileState] =
    useState<RequestState>(INITIAL_REQUEST_STATE);
  const isAuth0 =
    provider === AuthProvider.AUTH0 || provider === AuthProvider.AUTH0_DESKTOP;

  const handleGetOnboardingSessions = async () => {
    setOnboardingState({ isLoading: true });
    try {
      const data = await getIntegrationOnboardingSessions({ params: {} });
      setOnboardingState({ data, isLoading: false });
    } catch (error) {
      setOnboardingState({
        error: getErrorMessage(error, intl.formatMessage(messages.onboardingError)),
        isLoading: false,
      });
    }
  };

  const handleGetLaunchProfiles = async () => {
    setLaunchProfileState({ isLoading: true });
    try {
      const endpoint = isAuth0
        ? '/ehr-launch/onboarding/user-ehr-launch-profile'
        : '/ehr-launch/onboarding/user-ehr-launch-profile/v2';
      const { data } = isAuth0
        ? await getUserEHRLaunchProfileAuth0()
        : await getUserEHRLaunchProfile();
      setLaunchProfileState({
        data: {
          auth_provider: provider,
          endpoint,
          response: data,
        },
        isLoading: false,
      });
    } catch (error) {
      setLaunchProfileState({
        error: getErrorMessage(error, intl.formatMessage(messages.launchProfileError)),
        isLoading: false,
      });
    }
  };

  return (
    <section aria-labelledby="dev-tools-integration-account-heading" className="mt-5">
      <TypographyP2SemiBold id="dev-tools-integration-account-heading" className="mb-2">
        {intl.formatMessage(messages.heading)}
      </TypographyP2SemiBold>
      <div className="space-y-3">
        <TypographyCaption className="text-text-tertiary">
          {intl.formatMessage(messages.description)}
        </TypographyCaption>
        <TypographyCaption className="text-text-secondary">
          {intl.formatMessage(messages.authProvider, { provider })}
        </TypographyCaption>

        <div className="space-y-3">
          <div className="space-y-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              disabled={onboardingState.isLoading}
              onClick={() => {
                void handleGetOnboardingSessions();
              }}
            >
              {intl.formatMessage(messages.getOnboardingSessions)}
            </Button>
            <IntegrationDebugJsonResult
              title={intl.formatMessage(messages.onboardingResult)}
              data={onboardingState.data}
              error={onboardingState.error}
              isLoading={onboardingState.isLoading}
            />
          </div>

          <div className="space-y-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              disabled={launchProfileState.isLoading}
              onClick={() => {
                void handleGetLaunchProfiles();
              }}
            >
              {intl.formatMessage(messages.getEhrLaunchProfiles)}
            </Button>
            <IntegrationDebugJsonResult
              title={intl.formatMessage(messages.launchProfileResult)}
              data={launchProfileState.data}
              error={launchProfileState.error}
              isLoading={launchProfileState.isLoading}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
